import {useContext} from 'react';
import {getRemotionEnvironment} from './get-environment.js';
import {PreloadContext, setPreloads} from './prefetch-state.js';

export const usePreload = (src) => {
	const preloads = useContext(PreloadContext);

	return preloads[src] ?? src;
};


const blobToBase64 = function (blob) {
	const reader = new FileReader();

	return new Promise((resolve, reject) => {
		reader.onload = function () {
			const dataUrl = reader.result;
			resolve(dataUrl);
		};

		reader.onerror = (err) => {
			return reject(err);
		};

		reader.readAsDataURL(blob);
	});
};

/**
 * @description When you call the preFetch() function, an asset will be fetched and kept in memory so it is ready when you want to play it in a <Player>.
 *
 */
export const prefetch = (
	src,
	options
) => {
	const method = options?.method ?? 'blob-url';

	if (getRemotionEnvironment() === 'rendering') {
		return {
			free: () => undefined,
			waitUntilDone: () => Promise.resolve(src),
		};
	}

	let canceled = false;
	let objectUrl = null;
	let resolve = () => undefined;
	let reject  = () => undefined;

	const waitUntilDone = new Promise((res, rej) => {
		resolve = res;
		reject = rej;
	});

	const controller = new AbortController();
	let canBeAborted = true;

	fetch(src, {
		signal: controller.signal,
	})
		.then((res) => {
			canBeAborted = false;
			if (canceled) {
				return null;
			}

			if (!res.ok) {
				throw new Error(`HTTP error, status = ${res.status}`);
			}

			return res.blob();
		})
		.then((buf) => {
			if (!buf) {
				return;
			}

			if (method === 'base64') {
				return blobToBase64(buf);
			}

			return URL.createObjectURL(buf);
		})
		.then((url) => {
			if (canceled) {
				return;
			}

			objectUrl = url;

			setPreloads((p) => ({
				...p,
				[src]: objectUrl,
			}));
			resolve(objectUrl);
		})
		.catch((err) => {
			reject(err);
		});

	return {
		free: () => {
			if (objectUrl) {
				if (method === 'blob-url') {
					URL.revokeObjectURL(objectUrl);
				}

				setPreloads((p) => {
					const copy = {...p};
					delete copy[src];
					return copy;
				});
			} else {
				canceled = true;
				if (canBeAborted) {
					try {
						controller.abort();
					} catch (e) {}
				}
			}
		},
		waitUntilDone: () => {
			return waitUntilDone;
		},
	};
};
